import { useRef, useState } from "react";
import { api, extractError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Upload, X, Loader2, FileText } from "lucide-react";

/**
 * Reusable uploader.
 * kind: "image" | "video" | "document" | "any"
 * value: current URL (optional)
 * onChange: (fileMeta) => void  — { url, id, path, content_type, original_filename, size }
 */
export function FileUpload({ kind = "image", value, onChange, label, accept, className = "", testid = "file-upload", disabled }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const acceptAttr = accept || (
    kind === "image" ? "image/*" :
    kind === "video" ? "video/mp4,video/webm,video/quicktime" :
    kind === "document" ? ".pdf,.doc,.docx,.txt,.csv" : undefined
  );

  const pick = () => { if (!busy && !disabled) inputRef.current?.click(); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", kind);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.(data);
      toast.success("تم الرفع");
    } catch (err) {
      toast.error(extractError(err, "فشل رفع الملف"));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = (e) => { e.preventDefault(); e.stopPropagation(); onChange?.(null); };

  const displayUrl = value?.url ? (value.url.startsWith("http") ? value.url : `${process.env.REACT_APP_BACKEND_URL}${value.url}`) : (typeof value === "string" ? value : null);

  return (
    <div className={`space-y-2 ${className}`} data-testid={testid}>
      {label && <div className="text-sm font-medium">{label}</div>}
      <input ref={inputRef} type="file" accept={acceptAttr} className="hidden" onChange={onFile} data-testid={`${testid}-input`} />

      {kind === "image" && displayUrl ? (
        <div className="relative inline-block">
          <img src={displayUrl} alt="" className="max-h-40 rounded-lg border object-cover" />
          <button
            onClick={clear} type="button"
            className="absolute -top-2 -end-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground grid place-items-center shadow"
            data-testid={`${testid}-clear`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : displayUrl ? (
        <div className="flex items-center gap-2 p-2 rounded-lg border bg-secondary text-sm">
          <FileText className="h-4 w-4 shrink-0" />
          <a href={displayUrl} target="_blank" rel="noreferrer" className="flex-1 truncate text-primary hover:underline">
            {value?.original_filename || "ملف مرفوع"}
          </a>
          <button onClick={clear} type="button" className="text-destructive" data-testid={`${testid}-clear`}>
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      <Button type="button" variant="outline" onClick={pick} disabled={busy || disabled} className="gap-2 rounded-full" data-testid={`${testid}-button`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {busy ? "جارٍ الرفع..." : (displayUrl ? "استبدال الملف" : "اختر ملفًا")}
      </Button>
    </div>
  );
}

/** Helper: turn value returned by FileUpload into a URL string suitable for <img src>. */
export function fileToUrl(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (v.url) return v.url.startsWith("http") ? v.url : `${process.env.REACT_APP_BACKEND_URL}${v.url}`;
  return "";
}
